import { useCallback, useEffect, useRef } from 'react'
import { useAppStore } from '@core/store'
import { useJanelaAberta } from '@core/layout/useJanelaAberta'
import { ID_JANELA_INFO_DO_MODELO } from '@viewer/store/popupsSlice'
import ModelInfoPanel from '@viewer/inspector/ModelInfoPanel'

const MIN_W = 320
const MIN_H = 240
const MAX_W = 900
const MAX_H = 1000

// Floating window for the per-model overview (schema badge, units,
// federated model list, categories breakdown, IfcSystem/Group/Assembly
// browser). Singleton: only one is ever open. Triggered from the
// topbar's "Info do modelo" button so the right sidebar can be 100%
// dedicated to the selection's properties.
function FloatingModelInfoWindow() {
  const popup = useAppStore((s) => s.modelInfoPopup)
  const setOpen = useAppStore((s) => s.setModelInfoOpen)
  const movePopup = useAppStore((s) => s.moveModelInfoPopup)
  const resizePopup = useAppStore((s) => s.resizeModelInfoPopup)
  const bringToFront = useAppStore((s) => s.bringModelInfoToFront)
  const { z, trazer } = useJanelaAberta(ID_JANELA_INFO_DO_MODELO, popup.open)

  const dragOriginRef = useRef<{
    pointerStart: { x: number; y: number }
    posStart: { x: number; y: number }
  } | null>(null)
  const resizeOriginRef = useRef<{
    pointerStart: { x: number; y: number }
    sizeStart: { w: number; h: number }
  } | null>(null)

  const onTitleDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      bringToFront()
      dragOriginRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        posStart: { x: popup.position.x, y: popup.position.y },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [popup.position.x, popup.position.y, bringToFront],
  )

  const onResizeDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      e.stopPropagation()
      bringToFront()
      resizeOriginRef.current = {
        pointerStart: { x: e.clientX, y: e.clientY },
        sizeStart: { w: popup.size.w, h: popup.size.h },
      }
      ;(e.currentTarget as Element).setPointerCapture?.(e.pointerId)
    },
    [popup.size.w, popup.size.h, bringToFront],
  )

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (dragOriginRef.current) {
        const { pointerStart, posStart } = dragOriginRef.current
        const dx = e.clientX - pointerStart.x
        const dy = e.clientY - pointerStart.y
        const x = Math.min(
          Math.max(0, posStart.x + dx),
          window.innerWidth - 80,
        )
        const y = Math.min(
          Math.max(0, posStart.y + dy),
          window.innerHeight - 40,
        )
        movePopup({ x, y })
      }
      if (resizeOriginRef.current) {
        const { pointerStart, sizeStart } = resizeOriginRef.current
        const dx = e.clientX - pointerStart.x
        const dy = e.clientY - pointerStart.y
        const w = Math.min(MAX_W, Math.max(MIN_W, sizeStart.w + dx))
        const h = Math.min(MAX_H, Math.max(MIN_H, sizeStart.h + dy))
        resizePopup({ w, h })
      }
    }
    const onUp = () => {
      dragOriginRef.current = null
      resizeOriginRef.current = null
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [movePopup, resizePopup])

  if (!popup.open) return null

  return (
    <div
      role="dialog"
      aria-label="Info do modelo (janela flutuante)"
      className="fixed flex flex-col rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-2xl"
      style={{
        left: popup.position.x,
        top: popup.position.y,
        width: popup.size.w,
        height: popup.size.h,
        zIndex: z,
      }}
      onPointerDownCapture={trazer}
    >
      <div
        onPointerDown={onTitleDown}
        className="flex h-7 shrink-0 cursor-move items-center justify-between border-b border-[var(--color-border)] bg-[var(--color-panel)] px-2 text-xs text-[var(--color-text)] select-none"
      >
        <span className="truncate">Info do modelo</span>
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={() => setOpen(false)}
          className="rounded px-1.5 py-0 text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
          title="Fechar"
        >
          ×
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <ModelInfoPanel />
      </div>
      <div
        onPointerDown={onResizeDown}
        className="absolute bottom-0 right-0 h-3 w-3 cursor-se-resize"
        style={{
          backgroundImage:
            'linear-gradient(135deg, transparent 50%, var(--color-text-muted) 50%)',
          opacity: 0.6,
        }}
        title="Redimensionar"
      />
    </div>
  )
}

export default FloatingModelInfoWindow
